// Small form controls for the sketch inspector. Text fields commit on Enter or when they lose focus,
// and Escape puts the old value back, so each change is one step to undo.
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';

export const COMMON_TYPES = ['string', 'int', 'long', 'short', 'byte', 'bool', 'decimal', 'double', 'float', 'DateTime', 'DateTimeOffset', 'DateOnly', 'TimeOnly', 'TimeSpan', 'Guid', 'byte[]', 'char'];

export function TypeOptions() {
  return (
    <datalist id="mv-types">
      {COMMON_TYPES.map((t) => (
        <option key={t} value={t} />
      ))}
    </datalist>
  );
}

// `cn` only joins class names, so each input style is complete on its own (no conflicting overrides).
const INPUT_BASE = 'min-w-0 rounded-md border outline-none placeholder:text-faint focus:border-accent';
const VARIANTS = {
  boxed: 'border-border bg-bg px-2 hover:border-border-strong',
  inline: 'border-transparent bg-transparent px-1 hover:border-border focus:bg-bg',
};
const SIZES = { sm: 'h-6 pointer-coarse:h-8', md: 'h-7 pointer-coarse:h-9', lg: 'h-8 text-[14px] font-semibold pointer-coarse:h-10' };

function inputClass(o: { variant?: keyof typeof VARIANTS; size?: keyof typeof SIZES; width?: string; mono?: boolean; muted?: boolean }): string {
  const size = o.size ?? 'md';
  const text = size === 'lg' ? '' : o.mono ? 'font-mono text-[12px]' : 'text-[12.5px]';
  return cn(INPUT_BASE, VARIANTS[o.variant ?? 'boxed'], SIZES[size], text, o.muted ? 'text-muted focus:text-text' : 'text-text', o.width ?? 'w-full');
}

export function Row({ label, children, htmlFor }: { label: string; children: React.ReactNode; htmlFor?: string }) {
  return (
    <div className="flex items-center gap-3 py-[3px]">
      <label htmlFor={htmlFor} className="w-24 flex-none text-[12.5px] text-muted">
        {label}
      </label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/**
 * A text input that calls `onCommit` on Enter or blur when the value changed. `onCommit` returns
 * false when the value was not accepted, which puts the old one back.
 */
export function TextInput(props: {
  value: string;
  onCommit: (value: string) => boolean | void;
  placeholder?: string;
  mono?: boolean;
  list?: string;
  label?: string;
  id?: string;
  variant?: 'boxed' | 'inline';
  size?: 'sm' | 'md' | 'lg';
  /** Width classes (default: the full width). */
  width?: string;
  muted?: boolean;
  /** Focus and select the text whenever this number changes (and is not 0). */
  focusNonce?: number;
  onFocused?: () => void;
}) {
  const [text, setText] = useState(props.value);
  const ref = useRef<HTMLInputElement>(null);
  // Enter commits and then blurs, which would commit again before the new value arrives.
  const committed = useRef<string | null>(null);
  useEffect(() => {
    setText(props.value);
    committed.current = null;
  }, [props.value]);
  const { focusNonce, onFocused } = props;
  useEffect(() => {
    if (!focusNonce) return;
    ref.current?.focus();
    ref.current?.select();
    onFocused?.();
    // Only a new request focuses; `onFocused` is a new function on every render.
  }, [focusNonce]);
  const commit = () => {
    if (text === props.value || text === committed.current) return;
    committed.current = text;
    if (props.onCommit(text.trim()) === false) {
      committed.current = null;
      setText(props.value);
    }
  };
  return (
    <input
      ref={ref}
      id={props.id}
      value={text}
      list={props.list}
      aria-label={props.label}
      placeholder={props.placeholder}
      spellCheck={false}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          setText(props.value);
          e.currentTarget.blur();
        }
      }}
      className={inputClass(props)}
    />
  );
}

export function NumberInput(props: { value: number | undefined; onCommit: (value: number | null) => boolean | void; label?: string; min?: number; placeholder?: string }) {
  return (
    <TextInput
      value={props.value === undefined ? '' : String(props.value)}
      label={props.label}
      placeholder={props.placeholder ?? 'none'}
      onCommit={(text) => {
        if (text === '') return props.onCommit(null);
        const n = Number(text);
        if (!Number.isInteger(n) || n < (props.min ?? 0)) return false;
        return props.onCommit(n);
      }}
    />
  );
}

export function TextArea(props: { value: string; onCommit: (value: string) => void; placeholder?: string; label: string; rows?: number }) {
  const [text, setText] = useState(props.value);
  useEffect(() => setText(props.value), [props.value]);
  return (
    <textarea
      value={text}
      aria-label={props.label}
      placeholder={props.placeholder}
      rows={props.rows ?? 2}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text !== props.value && props.onCommit(text.trim())}
      className="w-full resize-y rounded-md border border-border bg-bg px-2 py-1.5 text-[12.5px] leading-snug text-text outline-none placeholder:text-faint hover:border-border-strong focus:border-accent"
    />
  );
}

export function Check(props: { checked: boolean; onChange: (checked: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <label className={cn('inline-flex items-center gap-2 text-[12.5px]', props.disabled ? 'text-faint' : 'text-text')}>
      <input type="checkbox" checked={props.checked} disabled={props.disabled} onChange={(e) => props.onChange(e.target.checked)} className="h-3.5 w-3.5 accent-[var(--accent)] pointer-coarse:h-5 pointer-coarse:w-5" />
      {props.label}
    </label>
  );
}

export function Select<T extends string>(props: { value: T; options: { value: T; label: string }[]; onChange: (value: T) => void; label?: string; id?: string }) {
  return (
    <select
      id={props.id}
      aria-label={props.label}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value as T)}
      className="h-7 w-full rounded-md border border-border bg-bg px-1.5 text-[12.5px] text-text outline-none hover:border-border-strong focus:border-accent pointer-coarse:h-9"
    >
      {props.options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function ActionButton(props: { onClick: () => void; children: React.ReactNode; danger?: boolean; title?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      title={props.title}
      disabled={props.disabled}
      onClick={props.onClick}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12.5px] disabled:opacity-40 pointer-coarse:h-9 pointer-coarse:px-3',
        props.danger ? 'border-removed/40 text-removed hover:bg-removed-soft' : 'border-border text-text hover:bg-hover',
      )}
    >
      {props.children}
    </button>
  );
}
